import { useEffect, useRef } from "react";
import axios from "axios";
import { getBackendUrl } from "@/utils/apiBase";

const API = `${getBackendUrl()}/api`;

/**
 * Hook to dynamically inject Meta Pixel and GA4 tracking scripts.
 * Fetches settings from the backend and injects scripts into <head>.
 * @param {object} options
 * @param {string} options.page - Page identifier for tracking events
 */
export const useTracking = ({ page } = {}) => {
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const init = async () => {
      try {
        const res = await axios.get(`${API}/settings/tracking`);
        const { meta_pixel_id, ga4_measurement_id, meta_pixel_enabled, ga4_enabled } = res.data;

        // Meta Pixel
        if (meta_pixel_enabled && meta_pixel_id) {
          injectMetaPixel(meta_pixel_id);
        }

        // GA4
        if (ga4_enabled && ga4_measurement_id) {
          injectGA4(ga4_measurement_id);
        }
      } catch (err) {
        // Silently fail - tracking should not break the page
        console.warn("Tracking init failed:", err.message);
      }
    };

    init();
  }, []);
};

function injectMetaPixel(pixelId) {
  if (window.fbq) return; // Already loaded

  // fbq initialization inline script
  const script = document.createElement("script");
  script.innerHTML = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${pixelId}');
    fbq('track', 'PageView');
  `;
  document.head.appendChild(script);

  // noscript fallback
  const noscript = document.createElement("noscript");
  const img = document.createElement("img");
  img.height = 1;
  img.width = 1;
  img.style.display = "none";
  img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
  noscript.appendChild(img);
  document.body.appendChild(noscript);
}

function injectGA4(measurementId) {
  if (document.querySelector(`script[src*="${measurementId}"]`)) return; // Already loaded

  // gtag.js script
  const gtagScript = document.createElement("script");
  gtagScript.async = true;
  gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(gtagScript);

  // gtag config inline script
  const configScript = document.createElement("script");
  configScript.innerHTML = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${measurementId}');
  `;
  document.head.appendChild(configScript);
}

/**
 * Fire a Meta Pixel conversion event (e.g., Lead, CompleteRegistration)
 */
export const trackMetaEvent = (eventName, params = {}) => {
  if (window.fbq) {
    window.fbq("track", eventName, params);
  }
};

/**
 * Fire a GA4 custom event
 */
export const trackGA4Event = (eventName, params = {}) => {
  if (window.gtag) {
    window.gtag("event", eventName, params);
  }
};
